/**
 * פאנל "העדפות עוגיות" — דיאלוג מודאלי. נפתח מ"התאמה אישית" בבאנר ומ"העדפות
 * עוגיות" ב-Footer.
 *
 * ⭐ מלכודת הפוקוס / `Esc` / החזרת הפוקוס מבוססים על אותו דפוס בדיוק כמו
 *    `AccessibilityWidget.tsx` (4.6) — ⛔ לא ספרייה חיצונית. שלושת המימושים
 *    העצמאיים באתר לדיאלוג נגיש: `Lightbox`, `AccessibilityWidget`, וזה.
 *
 * ⭐ הפקדים הם `<input type="checkbox">` נייטיביים עם `<label>` עוטף — נגישות
 *    מלאה במקלדת ובקורא מסך בלי קוד מותאם.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from '@phosphor-icons/react';
import { useCookieConsent } from './CookieConsentContext.js';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

export function CookieConsentManager() {
  const { isManagerOpen, closeManager, stored, acceptAll, save } = useCookieConsent();
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<Element | null>(null);

  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // ⭐ בכל פתיחה — מיישרים את המתגים לבחירה השמורה (או לברירת מחדל כבויה),
  //    שומרים את הטריגר להחזרת פוקוס, וממקדים את הדיאלוג.
  useEffect(() => {
    if (!isManagerOpen) return;
    triggerRef.current = document.activeElement;
    setAnalytics(stored?.analytics ?? false);
    setMarketing(stored?.marketing ?? false);
    dialogRef.current?.focus();
  }, [isManagerOpen, stored]);

  useEffect(() => {
    if (!isManagerOpen) return;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === 'Tab') {
        const dialog = dialogRef.current;
        if (!dialog) return;
        const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
        if (focusable.length === 0) return;
        const first = focusable[0] as HTMLElement;
        const last = focusable[focusable.length - 1] as HTMLElement;
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // ⚠️ תלוי בכוונה רק ב-isManagerOpen — `close`/`dialogRef` נגזרים מ-refs
    //    יציבים, כמו ב-AccessibilityWidget.tsx (4.6).
  }, [isManagerOpen]);

  function close() {
    closeManager();
    if (triggerRef.current instanceof HTMLElement) triggerRef.current.focus();
  }

  if (!isManagerOpen) return null;

  return (
    <div
      className="consent-dialog-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="consent-dialog"
        tabIndex={-1}
      >
        <div className="flex items-center justify-between">
          <h2 id={titleId} className="text-lg font-bold">
            העדפות עוגיות
          </h2>
          <button type="button" className="a11y-panel-close" onClick={close} aria-label="סגירה">
            <X size={18} weight="regular" aria-hidden="true" />
          </button>
        </div>

        <p id={descId} className="text-muted mt-2 text-sm">
          עוגיות חיוניות פעילות תמיד. עוגיות אנליטיקה ופרסום נטענות רק אם תאשרו אותן כאן, וניתן
          לשנות את הבחירה בכל עת.
        </p>

        <ul className="consent-cat-list">
          <li className="consent-cat">
            <div className="consent-cat-head">
              <span className="font-semibold">עוגיות חיוניות</span>
              <span className="consent-cat-always">פעיל תמיד</span>
            </div>
            <p className="text-muted text-sm">
              נדרשות לתפעול האתר ולאבטחתו, לשמירת עגלת הבקשות ולזכירת העדפות הנגישות. לא ניתן לכבות
              אותן.
            </p>
          </li>

          <li className="consent-cat">
            <label className="consent-cat-toggle">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(event) => setAnalytics(event.target.checked)}
              />
              <span>הפעלת עוגיות אנליטיקה</span>
            </label>
            <p className="text-muted text-sm">
              מדידת אופן השימוש באתר לצורך שיפורו. לא משמשות לזיהוי אישי.
            </p>
          </li>

          <li className="consent-cat">
            <label className="consent-cat-toggle">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(event) => setMarketing(event.target.checked)}
              />
              <span>הפעלת עוגיות פרסום ושיווק</span>
            </label>
            <p className="text-muted text-sm">
              עוגיות ופיקסלים של Google Ads ו-Meta Ads למדידת קמפיינים ולהצגת פרסום מותאם.
            </p>
          </li>
        </ul>

        <div className="consent-dialog-actions">
          <button
            type="button"
            className="btn btn-cta"
            onClick={() => save({ analytics, marketing })}
          >
            שמירת ההעדפות
          </button>
          <button type="button" className="btn btn-secondary" onClick={acceptAll}>
            קבלת הכל
          </button>
          <Link to="/privacy" className="text-link underline" onClick={close}>
            מדיניות הפרטיות
          </Link>
        </div>
      </div>
    </div>
  );
}
